<template>
    <modal name="request-money" height="auto" width="60%" :scrollable="true">
        <div class="modal-header-custom">
            <h3 class="modal-title-custom">{{ $t("wallet.requestPayment") || "Demander un paiement" }}</h3>
            <button @click="hide('request-money')" class="btn btn-close">
                <i class="mdi mdi-close"></i>
            </button>
        </div>

        <div class="loader" v-if="loader"></div>

        <div class="modal-body-custom">
            <div v-if="error !== ''" class="alert alert-danger">
                <i class="mdi mdi-alert-circle"></i>
                {{ error }}
            </div>

            <!-- Bank Account Information Section -->
            <div class="form-section">
                <h4 class="section-title">
                    <i class="mdi mdi-bank"></i>
                    {{ $t("wallet.bankAccountInfo") }}
                </h4>

                <div v-if="paymentInfos.length > 0" class="saved-accounts-section">
                    <label class="form-label-custom">{{ $t("wallet.chooseBankAccount") }}</label>
                    <div class="saved-accounts-list">
                        <div
                            v-for="information in paymentInfos"
                            :key="information.id"
                            class="saved-account-item"
                            :class="{ 'selected': selectedPaymentInformation.id === information.id }"
                            @click="checkPaymentInformation(information.id)"
                        >
                            <div class="account-radio">
                                <input
                                    type="radio"
                                    :checked="selectedPaymentInformation.id === information.id"
                                    :value="information.id"
                                />
                            </div>
                            <div class="account-details">
                                <div class="account-label">
                                    <i class="mdi mdi-credit-card"></i>
                                    <span class="account-text">IBAN: {{ information.iban }}</span>
                                </div>
                                <div class="account-sublabel">BIC: {{ information.bic }}</div>
                            </div>
                            <div class="account-check" v-if="selectedPaymentInformation.id === information.id">
                                <i class="mdi mdi-check-circle"></i>
                            </div>
                        </div>
                    </div>
                    <div class="divider-with-text">
                        <span>{{ $t("main.or") || "OU" }}</span>
                    </div>
                </div>

                <div class="admin-form-group">
                    <label class="admin-form-label">
                        IBAN <span class="required">*</span>
                    </label>
                    <input
                        type="text"
                        class="admin-form-input"
                        :class="{ 'input-error': has_error && errors.iban }"
                        v-model="selectedPaymentInformation.iban"
                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                    />
                    <span class="error-message" v-if="has_error && errors.iban">
                        <i class="mdi mdi-alert-circle"></i>
                        {{ errors.iban[0] }}
                    </span>
                </div>

                <div class="admin-form-group">
                    <label class="admin-form-label">
                        BIC <span class="required">*</span>
                    </label>
                    <input
                        type="text"
                        class="admin-form-input"
                        :class="{ 'input-error': has_error && errors.bic }"
                        v-model="selectedPaymentInformation.bic"
                        placeholder="BNPAFRPP"
                    />
                    <span class="error-message" v-if="has_error && errors.bic">
                        <i class="mdi mdi-alert-circle"></i>
                        {{ errors.bic[0] }}
                    </span>
                </div>
            </div>

            <!-- Amount Section -->
            <div class="form-section">
                <h4 class="section-title">
                    <i class="mdi mdi-cash-multiple"></i>
                    {{ $t("wallet.amountToBeCashed") }}
                </h4>

                <div class="balance-info">
                    <div class="balance-card">
                        <span class="balance-label">{{ $t("wallet.balance") || "Solde disponible" }}</span>
                        <span class="balance-amount">
                            {{ unpaidAmount | currency(" DA", 0, { symbolOnLeft: false }) }}
                        </span>
                    </div>
                </div>

                <div class="admin-form-group">
                    <label class="admin-form-label" :class="{ 'label-error': has_error && errors.amount }">
                        {{ $t("wallet.amount") }} <span class="required">*</span>
                    </label>
                    <div class="input-with-icon">
                        <i class="mdi mdi-currency-usd input-icon"></i>
                        <input
                            type="number"
                            class="admin-form-input input-with-padding"
                            :class="{ 'input-error': has_error && errors.amount }"
                            v-model="amount"
                            placeholder="50000"
                            min="0"
                            :max="unpaidAmount"
                        />
                        <span class="input-suffix">DA</span>
                    </div>
                    <span class="error-message" v-if="has_error && errors.amount">
                        <i class="mdi mdi-alert-circle"></i>
                        {{ errors.amount[0] }}
                    </span>
                    <span class="admin-form-hint">
                        <i class="mdi mdi-information"></i>
                        Montant minimum: 1000 DA
                    </span>
                </div>
            </div>
        </div>

        <div class="modal-footer-custom">
            <button @click="hide('request-money')" class="btn btn-secondary">
                <i class="mdi mdi-close"></i>
                {{ $t("action.cancel") }}
            </button>
            <button @click="requestMoney()" class="btn btn-primary">
                <i class="mdi mdi-check"></i>
                {{ $t("action.confirm") }}
            </button>
        </div>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </modal>
</template>

<script>
export default {
    props: {
        unpaidAmount: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            has_error: false,
            error: "",
            errors: {},
            loader: false,
            amount: 0,
            paymentInfos: [],
            selectedPaymentInformation: {
                id: null,
                iban: "",
                bic: ""
            },
        };
    },

    mounted() {
        this.getPaymentInfos();
    },

    methods: {
        hide(modalName) {
            event.preventDefault();
            this.$modal.hide(modalName);
        },

        checkPaymentInformation(paymentInformationId) {
            if (this.selectedPaymentInformation.id === paymentInformationId) {
                this.selectedPaymentInformation = {
                    id: null,
                    iban: "",
                    bic: ""
                };
            } else {
                this.selectedPaymentInformation = this.paymentInfos.filter(
                    (i) => i.id === paymentInformationId
                )[0];
            }
        },

        requestMoney() {
            this.error = "";
            this.has_error = false;
            this.errors = {};

            // Validation
            if (parseFloat(this.amount) === 0) {
                this.error = "Veuillez mettre un prix !";
                return false;
            }

            if (
                !this.selectedPaymentInformation.id &&
                (!this.selectedPaymentInformation.iban || !this.selectedPaymentInformation.bic)
            ) {
                this.error = "Veuillez donner vos coordonnées bancaires !";
                return false;
            }

            if (parseFloat(this.amount) > this.unpaidAmount) {
                this.error = "Vous ne disposez pas de ce montant";
                return false;
            }

            // Prepare data
            const data = {
                montant: parseFloat(this.amount)
            };

            if (this.selectedPaymentInformation.id) {
                data.payment_information_id = this.selectedPaymentInformation.id;
            } else {
                data.payment_information_id = -1;
                data.iban = this.selectedPaymentInformation.iban;
                data.bic = this.selectedPaymentInformation.bic;
            }

            this.loader = true;

            this.$axios.post("wallet/addPaymentRequest", data)
                .then(() => {
                    this.loader = false;
                    this.$notify({
                        group: "success",
                        title: "Demande envoyée avec succès !",
                        type: "success",
                    });
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                })
                .catch((err) => {
                    this.loader = false;
                    this.has_error = true;
                    
                    if (err.response?.data?.errors) {
                        this.errors = err.response.data.errors;
                    }
                    
                    this.$notify({
                        group: "error",
                        title: "Erreur !",
                        type: "error",
                        text: this.$t(err.response?.data?.message) || "Une erreur est survenue",
                    });
                });
        },

        async getPaymentInfos() {
            try {
                const response = await this.$axios.get("wallet/paymentInfo");
                this.paymentInfos = response.data;
            } catch (err) {
                console.error("Error loading payment infos:", err);
            }
        },
    },

    computed: {
        user: {
            get() {
                return this.$store.state.user;
            },
            set(user) {
                this.$store.commit("setUser", { user: user });
            },
        },
    },
};
</script>

<style scoped>
/* Modal Structure */
.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px;
    border-bottom: 1px solid #e5e7eb;
    background: white;
}

.modal-title-custom {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
}

.modal-body-custom {
    padding: 32px;
    max-height: 70vh;
    overflow-y: auto;
}

.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 32px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

/* Form Sections */
.form-section {
    margin-bottom: 32px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 20px;
}

.section-title i {
    font-size: 20px;
    color: #3b82f6;
}

/* Saved Accounts */
.saved-accounts-section {
    margin-bottom: 24px;
}

.form-label-custom {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
}

.saved-accounts-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
}

.saved-account-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: white;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.saved-account-item:hover {
    border-color: #3b82f6;
    background: #eff6ff;
}

.saved-account-item.selected {
    border-color: #3b82f6;
    background: #eff6ff;
}

.account-radio {
    display: flex;
    align-items: center;
}

.account-radio input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.account-details {
    flex: 1;
}

.account-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #111827;
    font-size: 14px;
}

.account-label i {
    color: #3b82f6;
}

.account-sublabel {
    font-size: 13px;
    color: #6b7280;
    margin-top: 4px;
}

.account-check {
    color: #10b981;
    font-size: 24px;
}

/* Divider */
.divider-with-text {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 24px 0;
}

.divider-with-text::before,
.divider-with-text::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
}

.divider-with-text span {
    padding: 0 16px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
}

/* Form Inputs */
.admin-form-group {
    margin-bottom: 20px;
}

.admin-form-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.admin-form-label.label-error {
    color: #dc2626;
}

.required {
    color: #dc2626;
    margin-left: 4px;
}

.admin-form-input {
    width: 100%;
    padding: 10px 14px;
    font-size: 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: all 0.2s;
}

.admin-form-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.admin-form-input.input-error {
    border-color: #dc2626;
}

.admin-form-input.input-error:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.input-with-icon {
    position: relative;
}

.input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 18px;
}

.input-with-padding {
    padding-left: 40px;
    padding-right: 50px;
}

.input-suffix {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-weight: 600;
    font-size: 14px;
}

.error-message {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #dc2626;
    font-size: 13px;
    margin-top: 6px;
}

.admin-form-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #6b7280;
    font-size: 13px;
    margin-top: 6px;
}

/* Balance Info */
.balance-info {
    margin-bottom: 20px;
}

.balance-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border-radius: 8px;
    color: white;
}

.balance-label {
    font-size: 14px;
    font-weight: 500;
}

.balance-amount {
    font-size: 20px;
    font-weight: 700;
}

/* Alert */
.alert {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
}

.alert-danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
}

.alert i {
    font-size: 20px;
}

/* Buttons */
.btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
}

.btn i {
    font-size: 16px;
}

.btn-close {
    background: transparent;
    color: #6b7280;
    padding: 8px;
}

.btn-close:hover {
    background: #f3f4f6;
    color: #111827;
}

.btn-secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-primary {
    background: #3b82f6;
    color: white;
}

.btn-primary:hover {
    background: #2563eb;
}

/* Loader */
.loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
}

/* Responsive */
@media (max-width: 768px) {
    .modal-header-custom,
    .modal-body-custom,
    .modal-footer-custom {
        padding: 20px;
    }

    .modal-footer-custom {
        flex-direction: column;
    }

    .btn {
        width: 100%;
        justify-content: center;
    }
}
</style>